import type {
  ClientMemexWorkflow,
  MemexWorkflowConfiguration,
  MemexWorkflowPersisted,
} from '../../api/workflows/contracts'
import {findWorkflowIndex, MemexWorkflowConverter, workflowTypeAsIdentity} from '../../helpers/workflow-utilities'

/**
 * Get clientIds of default workflows
 *
 * @param workflows - current workflows in the state
 * @returns all workflows to be displayed
 */
function getDefaultWorkflowClientIds(workflows: Array<ClientMemexWorkflow>): Map<string, string> {
  const defaultWorkflowClientIdMap = new Map<string, string>()

  for (const workflow of workflows) {
    const key = workflowTypeAsIdentity(workflow)
    if (!defaultWorkflowClientIdMap.has(key) && !workflow.isUserWorkflow) {
      defaultWorkflowClientIdMap.set(key, workflow.clientId)
    }
  }

  return defaultWorkflowClientIdMap
}

/**
 * Sorts workflows by enabled status (enabled first) then alphabetically by name
 *
 * @param workflows - workflows to sort
 * @returns sorted workflows array
 */
export function sortWorkflowsByEnabled(workflows: Array<ClientMemexWorkflow>): Array<ClientMemexWorkflow> {
  return workflows.sort((a, b) => {
    const aEnabledValue = a.enabled ? 0 : 1
    const bEnabledValue = b.enabled ? 0 : 1

    if (aEnabledValue !== bEnabledValue) {
      return aEnabledValue - bEnabledValue
    }

    return a.name.localeCompare(b.name)
  })
}

/**
 * Merges default workflow configurations with the persisted workflows
 *
 * @param currentWorkflows - current workflows in the state
 * @param workflowConfigurations - default workflow configurations from the server
 * @param createdWorkflows - persisted workflows from the server
 * @returns workflows sorted by enabled status then alphabetically
 */
export function mergeInitialWorkflows(
  currentWorkflows: Array<ClientMemexWorkflow>,
  workflowConfigurations: Array<MemexWorkflowConfiguration>,
  createdWorkflows: Array<MemexWorkflowPersisted>,
): Array<ClientMemexWorkflow> {
  const defaultWorkflowClientIdMap = getDefaultWorkflowClientIds(currentWorkflows)

  // The set of workflows starts as the list of system workflow configurations
  const allWorkflows = workflowConfigurations.map(({defaultWorkflow}) =>
    MemexWorkflowConverter.toClient(
      defaultWorkflow,
      false,
      defaultWorkflowClientIdMap.get(workflowTypeAsIdentity(defaultWorkflow)),
    ),
  )

  const numWorkflowsByType = new Map<string, number>()

  // For each workflow on the server, either replace the configuration with the persisted version
  // or append the workflow as an additional user workflow
  for (const createdWorkflow of createdWorkflows) {
    const key = workflowTypeAsIdentity(createdWorkflow)
    const numExistingWorkflows = numWorkflowsByType.get(key) || 0
    const isFirstOfType = numExistingWorkflows === 0

    const workflow = MemexWorkflowConverter.toClient(createdWorkflow, !isFirstOfType, createdWorkflow.id.toString())
    const workflowConfigIndex = findWorkflowIndex(allWorkflows, workflow, key)
    const hasConfigOfSameType = workflowConfigIndex >= 0

    if (hasConfigOfSameType && isFirstOfType) {
      // Replace the workflow configuration template with the persisted version
      allWorkflows[workflowConfigIndex] = workflow
    } else {
      allWorkflows.push(workflow)
    }

    numWorkflowsByType.set(key, numExistingWorkflows + 1)
  }

  return sortWorkflowsByEnabled(allWorkflows)
}
